'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';

type LoginForm = {
  email: string;
  password: string;
  remember: boolean;
};

type SignupForm = {
  displayName: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
};

// TODO: SECURITY — remove hardcoded admin bypass before launch
const ADMIN_ACCESS_CODE = '1234';

const demoCredentials = [
  { role: 'Admin', email: 'admin@coedparc.wales', password: ADMIN_ACCESS_CODE },
  { role: 'Visitor', email: 'margaret.williams@example.co.uk', password: 'library1986' },
];

export default function AuthScreen() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const loginForm = useForm<LoginForm>({ defaultValues: { remember: false } });
  const signupForm = useForm<SignupForm>();

  const autofillCredentials = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
  };

  const handleLogin = async (data: LoginForm) => {
    setSubmitting(true);
    try {
      // Use real Supabase signInWithPassword — no client-side password bypass
      const result = await signIn(data.email, data.password);
      toast.success('Welcome back!');
      // Route based on the authenticated user's actual role from user_profiles
      const profile = result?.profile;
      if (profile?.role === 'admin') {
        router.push('/admin-dashboard');
      } else {
        router.push('/share-your-story');
      }
      router.refresh();
    } catch (error: any) {
      setSubmitting(false);
      loginForm.setError('password', {
        message: error?.message || 'Invalid email or password',
      });
    }
  };

  const handleSignup = async (data: SignupForm) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setSubmitting(true);
    try {
      await signUp(data.email, data.password, { displayName: data.displayName });
      toast.success('Account created — welcome to Coed Parc');
      router.push('/share-your-story');
      router.refresh();
    } catch (error: any) {
      setSubmitting(false);
      signupForm.setError('email', {
        message: error?.message || 'Could not create account. Please try again.',
      });
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <AppImage
          src="/assets/images/image-1787352390071.png"
          alt="Coed Parc historic house at sunset surrounded by mature trees in Bridgend Wales"
          fill
          priority
          className="object-cover"
          sizes="50vw"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <Link href="/" className="flex items-center gap-3 no-underline">
            <AppLogo size={36} />
            <div>
              <span className="font-serif text-base font-semibold text-white block">Coed Parc</span>
              <span className="font-sans text-xs text-white/60 tracking-wider uppercase">Bridgend · Est. 1899</span>
            </div>
          </Link>
          <div>
            <blockquote className="font-serif text-2xl italic text-white/90 leading-relaxed mb-6">
              &ldquo;This is not simply the restoration of a building. It is the preservation of a piece of Bridgend&apos;s history.&rdquo;
            </blockquote>
            <p className="font-sans text-sm text-white/55 tracking-wide">Grade II Listed · Built 1899</p>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 bg-background flex flex-col justify-center px-8 md:px-16 lg:px-20 py-12">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <AppLogo size={32} />
          <span className="font-serif text-base font-semibold text-foreground">Coed Parc</span>
        </div>

        <div className="max-w-sm w-full mx-auto">
          {/* Mode toggle */}
          <div className="flex gap-6 mb-8 border-b border-border">
            {(['login', 'signup'] as const).map((m) => (
              <button
                key={`auth-mode-${m}`}
                onClick={() => setMode(m)}
                className={`font-sans text-sm font-medium pb-3 border-b-2 transition-colors ${
                  mode === m
                    ? 'border-accent text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                style={{ marginBottom: '-1px' }}
              >
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {mode === 'login' ? (
            <>
              <h1 className="font-serif text-2xl font-semibold text-foreground mb-2">Welcome back</h1>
              <p className="font-sans text-sm text-muted-foreground mb-8">
                Sign in to share your Coed Parc story or manage submissions.
              </p>
              <form onSubmit={loginForm.handleSubmit(handleLogin)} noValidate className="flex flex-col gap-5">
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Email address
                  </label>
                  <input
                    type="email"
                    className={`input-field ${loginForm.formState.errors.email ? 'input-error' : ''}`}
                    placeholder="your@email.com"
                    {...loginForm.register('email', {
                      required: 'Please enter your email address',
                      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Please enter a valid email address' },
                    })}
                  />
                  {loginForm.formState.errors.email && (
                    <p className="font-sans text-xs text-red-600 mt-1">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`input-field pr-11 ${loginForm.formState.errors.password ? 'input-error' : ''}`}
                      placeholder="Your password"
                      {...loginForm.register('password', { required: 'Please enter your password' })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="font-sans text-xs text-red-600 mt-1">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="remember"
                    className="w-4 h-4 accent-primary"
                    {...loginForm.register('remember')}
                  />
                  <label htmlFor="remember" className="font-sans text-sm text-muted-foreground cursor-pointer">
                    Remember me
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed w-full"
                >
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : 'Sign In'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-serif text-2xl font-semibold text-foreground mb-2">Create an account</h1>
              <p className="font-sans text-sm text-muted-foreground mb-8">
                Join the Coed Parc community and share your memories.
              </p>
              <form onSubmit={signupForm.handleSubmit(handleSignup)} noValidate className="flex flex-col gap-5">
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Display name <span className="text-red-500">*</span>
                  </label>
                  <p className="font-sans text-xs text-muted-foreground mb-1.5">This is how your name will appear on published stories.</p>
                  <input
                    type="text"
                    className={`input-field ${signupForm.formState.errors.displayName ? 'input-error' : ''}`}
                    placeholder="e.g. Margaret Williams"
                    {...signupForm.register('displayName', { required: 'Please enter a display name' })}
                  />
                  {signupForm.formState.errors.displayName && (
                    <p className="font-sans text-xs text-red-600 mt-1">{signupForm.formState.errors.displayName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Email address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    className={`input-field ${signupForm.formState.errors.email ? 'input-error' : ''}`}
                    placeholder="your@email.com"
                    {...signupForm.register('email', {
                      required: 'Please enter your email address',
                      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Please enter a valid email address' },
                    })}
                  />
                  {signupForm.formState.errors.email && (
                    <p className="font-sans text-xs text-red-600 mt-1">{signupForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`input-field pr-11 ${signupForm.formState.errors.password ? 'input-error' : ''}`}
                      placeholder="Choose a password"
                      {...signupForm.register('password', {
                        required: 'Please choose a password',
                        minLength: { value: 8, message: 'Password must be at least 8 characters' },
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.password && (
                    <p className="font-sans text-xs text-red-600 mt-1">{signupForm.formState.errors.password.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-foreground mb-1.5">
                    Confirm password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      className={`input-field pr-11 ${signupForm.formState.errors.confirmPassword ? 'input-error' : ''}`}
                      placeholder="Repeat your password"
                      {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    >
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.confirmPassword && (
                    <p className="font-sans text-xs text-red-600 mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="terms"
                    className="w-4 h-4 accent-primary mt-0.5 shrink-0"
                    {...signupForm.register('terms', { required: 'You must accept the terms to continue' })}
                  />
                  <label htmlFor="terms" className="font-sans text-xs text-muted-foreground cursor-pointer leading-relaxed">
                    I agree to the{' '}
                    <a href="#" className="text-accent underline underline-offset-2 hover:text-foreground transition-colors">terms of service</a>{' '}
                    and{' '}
                    <a href="#" className="text-accent underline underline-offset-2 hover:text-foreground transition-colors">privacy policy</a>.
                  </label>
                </div>
                {signupForm.formState.errors.terms && (
                  <p className="font-sans text-xs text-red-600 -mt-3">{signupForm.formState.errors.terms.message}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed w-full"
                >
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Creating account…</> : 'Create Account'}
                </button>
              </form>
            </>
          )}

          {/* Demo credentials box */}
          <div className="mt-8 bg-secondary border border-border rounded-sm p-4">
            <p className="font-sans text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Demo Accounts
            </p>
            <div className="flex flex-col gap-2">
              {demoCredentials.map((cred) => (
                <div
                  key={`demo-${cred.role}`}
                  className="flex items-center justify-between bg-card border border-border rounded px-3 py-2 gap-3"
                >
                  <div className="min-w-0">
                    <p className="font-sans text-xs font-semibold text-accent">{cred.role}</p>
                    <p className="font-sans text-xs text-muted-foreground truncate">{cred.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => autofillCredentials(cred.email, cred.password)}
                    className="font-sans text-xs font-medium text-primary hover:text-accent transition-colors shrink-0 underline underline-offset-2"
                  >
                    Use
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}